interface Props {
  date: Date; // any date in the current month to display
  appointmentCounts: Map<string, number>; // "YYYY-MM-DD" -> count
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
}

const DAY_HEADERS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function toISO(d: Date): string {
  // Local-TZ date string — matches the calendar header rather than the UTC
  // date for users near midnight. Mirrors the Calendar page's `toISO`.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getGridStartDate(year: number, month: number): Date {
  const firstDay = new Date(year, month, 1);
  const dayOfWeek = firstDay.getDay();
  const offset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const start = new Date(firstDay);
  start.setDate(start.getDate() + offset);
  return start;
}

export function MonthCalendar({ date, appointmentCounts, selectedDate, onSelectDate }: Props) {
  const year = date.getFullYear();
  const month = date.getMonth();

  const gridStart = getGridStartDate(year, month);
  const daysInMonth = getDaysInMonth(year, month);

  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const offset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
  const totalCells = Math.ceil((offset + daysInMonth) / 7) * 7;

  const cells: Date[] = [];
  for (let i = 0; i < totalCells; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }

  const todayStr = toISO(new Date());

  return (
    <div
      style={{
        background: "var(--paper-raised)",
        borderRadius: 14,
        boxShadow: "var(--shadow-sm)",
        overflow: "hidden",
      }}
    >
      {/* Day-of-week headers */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          background: "var(--paper-well)",
          borderBottom: "1px solid var(--rule)",
        }}
      >
        {DAY_HEADERS.map((d) => (
          <div
            key={d}
            style={{
              padding: "10px 0",
              textAlign: "center",
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 10.5,
              fontWeight: 500,
              color: "var(--ink-faint)",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
            }}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
        }}
      >
        {cells.map((cellDate, idx) => {
          const isoStr = toISO(cellDate);
          const isCurrentMonth = cellDate.getMonth() === month;
          const isToday = isoStr === todayStr;
          const isSelected = isoStr === selectedDate;
          const count = appointmentCounts.get(isoStr) ?? 0;
          const isLastRow = idx >= totalCells - 7;

          return (
            <button
              key={isoStr}
              onClick={() => onSelectDate(isSelected ? "" : isoStr)}
              style={{
                padding: "10px 6px",
                minHeight: 64,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 4,
                border: "none",
                borderRight: (idx + 1) % 7 === 0 ? "none" : "1px solid var(--rule)",
                borderBottom: isLastRow ? "none" : "1px solid var(--rule)",
                background: isToday
                  ? "var(--ink)"
                  : isSelected
                  ? "var(--paper-well)"
                  : "transparent",
                cursor: "pointer",
                transition: "background 120ms ease",
                position: "relative",
              }}
            >
              {/* Selection ring on non-today cells */}
              {isSelected && !isToday && (
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    inset: 0,
                    boxShadow: "inset 0 0 0 2px var(--ink)",
                    pointerEvents: "none",
                  }}
                />
              )}

              {/* Date numeral — serif for confidence */}
              <span
                style={{
                  fontFamily: "'Source Serif 4', Georgia, serif",
                  fontWeight: 500,
                  fontSize: 16,
                  lineHeight: 1,
                  fontFeatureSettings: "'tnum'",
                  color: isToday
                    ? "var(--canvas)"
                    : isCurrentMonth
                    ? "var(--ink)"
                    : "var(--ink-ghost)",
                }}
              >
                {cellDate.getDate()}
              </span>

              {/* Appointment count */}
              {count > 0 && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: isToday
                      ? "color-mix(in oklab, var(--canvas), transparent 30%)"
                      : "var(--ink-soft)",
                    fontFeatureSettings: "'tnum'",
                  }}
                >
                  {count}
                </span>
              )}

              {/* Today's accent dot */}
              {isToday && (
                <span
                  style={{
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    background: "var(--accent)",
                    marginTop: count > 0 ? 0 : 6,
                  }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
